<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { ChevronDownIcon, HistoryIcon, MonitorIcon, MoonIcon, PawPrintIcon, SunIcon } from '@lucide/vue';
import { ROLE_LABELS, initials, type Role } from '@yana/shared';
import { SECTIONS, type NavSection } from '@/lib/nav';
import PresenceIndicator from '@/components/layout/PresenceIndicator.vue';
import NotificationBell from '@/components/layout/NotificationBell.vue';
import MascotToggle from '@/components/mascot/MascotToggle.vue';
import { useAuthStore } from '@/stores/auth';
import { useThemeStore, type ThemeChoice } from '@/stores/theme';
import { useUndoStack } from '@/composables/useUndoStack';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Шапка приложения — одна на все страницы.
 *
 * Раньше шапка была только у таблицы, а каждый второй экран начинался
 * со своей строки заголовка и ссылки «← К таблице». При переходе верх
 * страницы перестраивался целиком: марка исчезала, профиль исчезал,
 * содержимое подпрыгивало. Теперь эта полоса стоит на месте всегда,
 * а меняется только то, что под ней.
 *
 * Высота фиксированная (48 px) и не зависит от содержимого — иначе при
 * переходе между разделами всё, что ниже, сдвигается на несколько пикселей.
 */

const auth = useAuthStore();
const theme = useThemeStore();
const undo = useUndoStack();
const route = useRoute();
const router = useRouter();

const allowed = computed(() => SECTIONS.filter((i) => !i.permission || auth.can(i.permission)));
const canOpenSettings = computed(() => allowed.value.some((item) => item.name === 'settings'));

/** Разделы со своим адресом. Листы раскрываются списком — они ниже. */
const items = computed(() => allowed.value.filter((i) => !i.dynamic));

/*
  Листы в шапке не показываются: их место — полоса ярлыков по низу экрана,
  как вкладки рабочей книги. Право на раздел всё равно перечислено
  в `SECTIONS` — по нему роутер закрывает адрес `/sheets/:code` для роли,
  которой листы не положены.
*/

/** Таблица — единственный раздел, куда ведёт корень; остальные по префиксу. */
function isCurrent(item: NavSection): boolean {
  if (item.to === '/') return route.path === '/' || route.path.startsWith('/candidates');
  return route.path.startsWith(item.to);
}

const THEMES: Array<{ value: ThemeChoice; label: string }> = [
  { value: 'light', label: 'Светлая' },
  { value: 'dark', label: 'Тёмная' },
  { value: 'system', label: 'Как в системе' },
];

async function logout(): Promise<void> {
  // История отмены — общая на приложение: не вычистив её, следующий
  // вошедший смог бы откатить правки предыдущего, ничего о них не зная
  undo.clear();
  try {
    await auth.logout();
  } finally {
    await router.replace({ name: 'login' });
  }
}
</script>

<template>
  <header
    data-mascot-header
    class="flex h-12 shrink-0 items-center gap-2 border-b border-border bg-app-header px-2 sm:px-3"
  >
    <RouterLink
      to="/"
      class="flex shrink-0 items-center gap-2 rounded-md px-1 py-0.5 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
    >
      <span
        class="flex size-6 shrink-0 items-center justify-center rounded bg-primary text-[10px] font-semibold text-primary-foreground"
        aria-hidden="true"
        >ЯН</span
      >
      <span class="hidden text-[14px] font-semibold whitespace-nowrap text-foreground sm:inline">
        Яна CRM
      </span>
    </RouterLink>

    <!-- Разделы: с планшета строкой, на телефоне — списком в меню.
         Подписи постоянной длины, ширина не пляшет от активного раздела -->
    <nav class="hidden min-w-0 items-center gap-0.5 lg:flex">
      <RouterLink
        v-for="item in items"
        :key="item.to"
        :to="item.to"
        data-mascot-menu-target
        class="flex h-8 shrink-0 cursor-pointer items-center rounded-md px-2.5 text-[12px] font-medium whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
        :class="
          isCurrent(item)
            ? 'bg-accent text-foreground'
            : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground'
        "
      >
        {{ item.label }}
      </RouterLink>

    </nav>

    <DropdownMenu>
      <DropdownMenuTrigger as-child>
        <Button variant="ghost" size="sm" class="lg:hidden" data-mascot-menu-target>
          Разделы
          <ChevronDownIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" class="max-h-[70vh] w-52 overflow-auto">
        <DropdownMenuItem v-for="item in items" :key="item.to" as-child>
          <RouterLink :to="item.to">{{ item.label }}</RouterLink>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    <div class="ml-auto flex shrink-0 items-center gap-1">
      <slot name="actions" />

      <!-- Кто ещё в базе и жива ли связь: вопрос про всё приложение,
           а не про таблицу, — поэтому здесь, а не в панели над ней -->
      <PresenceIndicator />
      <NotificationBell v-if="auth.user" :key="auth.user.id" />
      <MascotToggle />

      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <Button
            variant="ghost"
            size="icon-sm"
            :aria-label="`Оформление: ${THEMES.find((t) => t.value === theme.choice)?.label}`"
            :data-tip="`Оформление: ${THEMES.find((t) => t.value === theme.choice)?.label}`"
          >
            <SunIcon v-if="theme.choice === 'light'" />
            <MoonIcon v-else-if="theme.choice === 'dark'" />
            <MonitorIcon v-else />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" class="w-44">
          <DropdownMenuLabel class="text-[11px] font-normal text-muted-foreground">
            Оформление
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup
            :model-value="theme.choice"
            @update:model-value="(v) => theme.set(String(v ?? 'system') as ThemeChoice)"
          >
            <DropdownMenuRadioItem v-for="t in THEMES" :key="t.value" :value="t.value">
              {{ t.label }}
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
          <template v-if="canOpenSettings">
            <DropdownMenuSeparator />
            <DropdownMenuItem as-child>
              <RouterLink to="/settings/colors">Цветовая схема…</RouterLink>
            </DropdownMenuItem>
          </template>
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <button
            type="button"
            class="flex h-8 cursor-pointer items-center gap-2 rounded-md px-1 transition-colors hover:bg-accent sm:pr-1.5 sm:pl-2 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
            :aria-label="`Меню профиля: ${auth.user?.name ?? ''}`"
          >
            <span
              class="flex size-6 items-center justify-center rounded-full text-[11px] font-bold text-white"
              :style="{ background: auth.user?.color ?? '#64748b' }"
              >{{ initials(auth.user?.name ?? '') }}</span
            >
            <span
              class="hidden max-w-[120px] truncate text-[12px] font-medium text-foreground xl:inline"
            >
              {{ auth.user?.name }}
            </span>
            <ChevronDownIcon class="size-3.5 shrink-0 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" class="w-56">
          <DropdownMenuLabel class="text-[11px] font-normal text-muted-foreground">
            {{ auth.user?.name }} · {{ ROLE_LABELS[auth.role as Role] }}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem v-if="auth.user" as-child>
            <RouterLink to="/profile/pet"><PawPrintIcon class="size-4" />Питомец</RouterLink>
          </DropdownMenuItem>
          <DropdownMenuItem v-if="auth.user" as-child>
            <RouterLink to="/activity"><HistoryIcon class="size-4" />История действий</RouterLink>
          </DropdownMenuItem>
          <DropdownMenuItem variant="destructive" @select="logout">Выйти</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </header>

</template>
